<template>
	<div>
		<div v-if="!store.areAnyFeaturesSelected">
			<p>
				{{ $t("features.featureModeControls.selectFeatureHint") }}
			</p>
		</div>
		<div v-else-if="!store.areAllSelectedFeaturesOfSameType">
			<p>
				{{ $t("features.featureModeControls.differentTypesHint") }}
			</p>
		</div>
		<div v-else style="display: flex; flex-direction: column; gap: 4px">
			<!-- radio buttons for different processes -->
			<div style="display: flex; align-items: center">
				<input
					type="radio"
					name="process_choice"
					:value="ProcessType.Cut"
					v-model="store.targetProcessType"
					:disabled="!isEnabled(ProcessType.Cut)"
				/>
				<span style="margin-left: 8px">{{ getProcessTypeText(ProcessType.Cut) }}</span>
			</div>
			<div style="display: flex; align-items: center">
				<input
					type="radio"
					name="process_choice"
					:value="ProcessType.Engrave"
					v-model="store.targetProcessType"
					:disabled="!isEnabled(ProcessType.Engrave)"
				/>
				<span style="margin-left: 8px"
					>{{ getProcessTypeText(ProcessType.Engrave)
					}}{{ areCountersinksSelected ? $t("features.featureModeControls.andCutting") : "" }}</span
				>
			</div>
			<div style="display: flex; align-items: center" :title="disabledTooltip(ProcessType.FormScrewThread)">
				<input
					type="radio"
					name="process_choice"
					:value="ProcessType.FormScrewThread"
					v-model="store.targetProcessType"
					:disabled="!isEnabled(ProcessType.FormScrewThread)"
				/>
				<span style="margin-left: 8px">{{
					getProcessTypeText(ProcessType.FormScrewThread)
				}}</span>
			</div>
			<div style="display: flex; align-items: center" :title="disabledTooltip(ProcessType.FormExtrudedThread)">
				<input
					type="radio"
					name="process_choice"
					:value="ProcessType.FormExtrudedThread"
					v-model="store.targetProcessType"
					:disabled="!isEnabled(ProcessType.FormExtrudedThread)"
				/>
				<span style="margin-left: 8px">{{
					getProcessTypeText(ProcessType.FormExtrudedThread)
				}}</span>
			</div>
			<div style="display: flex; align-items: center" :title="disabledTooltip(ProcessType.FormCountersink)">
				<input
					type="radio"
					name="process_choice"
					:value="ProcessType.FormCountersink"
					v-model="store.targetProcessType"
					:disabled="!isEnabled(ProcessType.FormCountersink)"
				/>
				<span style="margin-left: 8px">{{
					getProcessTypeText(ProcessType.FormCountersink)
				}}</span>
			</div>
			<div style="display: flex; align-items: center" :title="disabledTooltip(ProcessType.DrillCylinderHeadBoltHole)">
				<input
					type="radio"
					name="process_choice"
					:value="ProcessType.DrillCylinderHeadBoltHole"
					v-model="store.targetProcessType"
					:disabled="!isEnabled(ProcessType.DrillCylinderHeadBoltHole)"
				/>
				<span style="margin-left: 8px">{{
					getProcessTypeText(ProcessType.DrillCylinderHeadBoltHole)
				}}</span>
			</div>
			<div style="display: flex; align-items: center" v-if="store.pressJoinParameter.length > 0" :title="disabledTooltip(ProcessType.PressJoin)">
				<input
					type="radio"
					name="process_choice"
					:value="ProcessType.PressJoin"
					v-model="store.targetProcessType"
					:disabled="!isEnabled(ProcessType.PressJoin)"
				/>
				<span style="margin-left: 8px">{{
					getProcessTypeText(ProcessType.PressJoin)
				}}</span>
			</div>
			<!-- settings for chosen process -->
			<div
				v-if="store.targetProcessType !== ProcessType.None"
				style="display: flex; flex-direction: column; gap: 4px; margin-top: 16px"
			>
				<div v-if="anySettingsShown">{{ $t("features.featureModeControls.settingsLabel") }}</div>

				<!-- radio buttons for surface -->
				<div
					v-if="surfaceButtonsShown"
					style="display: flex; flex-direction: column; gap: 4px; margin-top: 8px"
				>
					<div style="display: flex; align-items: center">
						<input
							type="radio"
							name="surface_choice"
							:value="Surface.Front"
							v-model="surface"
							@change="updateSurfaceContext"
							:disabled="store.article.isReadOnly"
						/>
						<span style="margin-left: 8px">{{ $t("common.featureType.anchorFace") }}</span>
					</div>
					<div style="display: flex; align-items: center">
						<input
							type="radio"
							name="surface_choice"
							:value="Surface.Back"
							v-model="surface"
							@change="updateSurfaceContext"
							:disabled="store.article.isReadOnly"
						/>
						<span style="margin-left: 8px">{{ $t("common.featureType.bottomFace") }}</span>
					</div>
				</div>

				<!-- Dropdown for the category in case of a press join-->
				<div v-if="pressJoinCategoryShown" style="margin-top: 8px">
					<select v-model="category" :disabled="store.article.isReadOnly">
						<option :value="null">{{ $t("features.featureModeControls.pressJoinCategories.all") }}</option>
						<option :value="0">{{ $t("features.featureModeControls.pressJoinCategories.nut") }}</option>
						<option :value="1">{{ $t("features.featureModeControls.pressJoinCategories.threadedStud") }}</option>
						<option :value="2">{{ $t("features.featureModeControls.pressJoinCategories.threadedBushing") }}</option>
					</select>
				</div>

				<!-- dropdown for the used tool -->
				<div v-if="toolDropdownShown" style="margin-top: 8px">
					<select v-model="toolId" :disabled="store.article.isReadOnly">
						<option :value="null">{{ $t("features.featureModeControls.pleaseSelectTool") }}</option>
						<option
							v-for="parameter in sortedAvailableFormingParameters"
							:value="parameter.toolId"
						>
							{{ parameter.bezeichnung }}
						</option>
					</select>
				</div>

				<!-- warning message if there is no usable tool for the selection -->
				<div v-if="noToolsForSelectedSideAvailable" style="margin-top: 8px">
					<p>{{ $t("features.featureModeControls.noToolsForSide") }}</p>
				</div>

				<!-- button for saving changes -->
				<div style="margin-top: 8px; text-align: center">
					<button
						class="blexon-button"
						@click="modifyFeatures()"
						:disabled="applyButtonDisabled"
					>
						{{ $t("features.featureModeControls.apply") }}
					</button>
				</div>
			</div>
		</div>
		<div
			v-if="
				!store.article.isReadOnly &&
				store.editorMode === EditorMode.WeldMode &&
				store.weldlines.length > 0
			"
		>
			<!-- weldlines, if any are shown right now -->
			<Weldlines></Weldlines>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useMainStore } from "@/store/mainStore";
import {
	EditorMode,
	LoadingState,
	MessageState,
	PressJoinCategory,
	ProcessType,
	Surface,
	getProcessTypeText,
} from "@/model/Enums";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { FeatureHelper } from "@/model/FeatureHelper";
import { FeatureType } from "@/model/FeatureType";
import { IFormingParameter } from "@/model/ParameterTypes";
import { BlexonErpApi } from "@/store/BlexonErpApi";
import { ITaskDto } from "@/model/ITaskDto";
import Weldlines from "./Weldlines.vue";

const store = useMainStore();
const { t } = useI18n();
const currentProcessType = ref<ProcessType>(ProcessType.None);
const surface = ref<Surface>(Surface.Front);
const availableFormingParameters = ref<IFormingParameter[]>([]);
const availableFormingParametersForSide = ref<IFormingParameter[]>([]);
const toolId = ref<string | null>(null);
const category = ref<PressJoinCategory | null>(null);

const sortedAvailableFormingParameters = computed(() => {
	const test = availableFormingParametersForSide.value
		.filter(p => category.value == null || store.targetProcessType != ProcessType.PressJoin || (p as any).category == category.value)
		.sort((a: any, b: any) => a.kernlochDurchmesser - b.kernlochDurchmesser);

	if (!test.some(p => p.toolId === toolId.value)) {
		toolId.value = null
	}
	return test;
});

// Adjust the context when the selected features change
const onSelectedFeaturesChanged = () => {
	if (store.areAnyFeaturesSelected && store.areAllSelectedFeaturesOfSameType) {
		// set the current process type of the selected feature
		const feature = FeatureHelper.create(store.selectedFeatures[0]);
		currentProcessType.value = feature.getProcessType();
		// set the target process type to the same value initially.
		// when the selected features change, this should always happen.
		store.targetProcessType = currentProcessType.value;
	} else {
		// If the selected features are not of the same type, reset some values.
		currentProcessType.value = ProcessType.None;
		store.targetProcessType = ProcessType.None;
	}
};

// Adjust context, when target process type changes.
const onTargetProcessChanged = () => {
	if (store.targetProcessType !== ProcessType.None) {
		// set the surface
		if (store.selectedFeatures[0].featureType === FeatureType.Hole) {
			// Holes have no side defined. That's why we use the front
			surface.value = Surface.Front;
		} else {
			surface.value = store.selectedFeatures[0].side ?? Surface.Front;
		}

		// get and set the parameters of the selected process type
		let parameters = FeatureHelper.getFormingParametersByProcessType(store.targetProcessType);
		availableFormingParameters.value = parameters;
		availableFormingParametersForSide.value = parameters;
		if (
			store.targetProcessType === ProcessType.FormCountersink ||
			store.targetProcessType === ProcessType.FormExtrudedThread ||
			store.targetProcessType === ProcessType.DrillCylinderHeadBoltHole
		) {
			// The side is only important for those three processes
			availableFormingParametersForSide.value = parameters.filter(
				(p) => p.side === surface.value
			);
		}



		// set the tool id
		if (store.useAllSelectedFeaturesSameTool) {
			if (store.targetProcessType === ProcessType.PressJoin) {
				var parameter = store.pressJoinParameter.find(p => p.toolId === store.selectedFeatures[0].toolId);
				if (parameter) {
					category.value = parameter.category;
				}
			}
			toolId.value =
				store.targetProcessType === ProcessType.Cut
					? null
					: store.selectedFeatures[0].toolId;
		} else {
			toolId.value = null;
		}
	} else {
		surface.value = Surface.Front;
		availableFormingParameters.value = [];
		availableFormingParametersForSide.value = [];
		toolId.value = null;
	}
};

const updateSurfaceContext = () => {
	if (
		availableFormingParameters.value &&
		(store.targetProcessType === ProcessType.FormCountersink ||
			store.targetProcessType === ProcessType.DrillCylinderHeadBoltHole ||
			store.targetProcessType === ProcessType.FormExtrudedThread)
	) {
		// The side is only important for those processes
		availableFormingParametersForSide.value = availableFormingParameters.value.filter(
			(p) => p.side === surface.value
		);
	} else if (store.targetProcessType === ProcessType.PressJoin) {
		// For press join, all parameters are available on both sides
		availableFormingParametersForSide.value = availableFormingParameters.value;
	} else {
		toolId.value = null;
	}

};

const isEnabled = (processType: ProcessType): boolean => {
	if (store.article.isReadOnly) return false;
	if (store.areAnyFeaturesSelected && store.areAllSelectedFeaturesOfSameType) {
		const feature = FeatureHelper.create(store.selectedFeatures[0]);
		if (feature.getPossibleTargetTypes().find((t) => t === processType) === undefined) {
			return false;
		}
		// Disable the radio button if there are no tools at all for the current material.
		if (
			processType === ProcessType.FormScrewThread ||
			processType === ProcessType.FormExtrudedThread ||
			processType === ProcessType.FormCountersink ||
			processType === ProcessType.DrillCylinderHeadBoltHole ||
			processType === ProcessType.PressJoin
		) {
			return FeatureHelper.getFormingParametersByProcessType(processType).length > 0;
		}
		return true;
	}
	return false;
};

const disabledTooltip = (processType: ProcessType): string => {
	if (
		processType !== ProcessType.FormScrewThread &&
		processType !== ProcessType.FormExtrudedThread &&
		processType !== ProcessType.FormCountersink &&
		processType !== ProcessType.DrillCylinderHeadBoltHole &&
		processType !== ProcessType.PressJoin
	) {
		return "";
	}
	if (store.article.isReadOnly) return "";
	if (!store.areAnyFeaturesSelected || !store.areAllSelectedFeaturesOfSameType) return "";
	if (FeatureHelper.getFormingParametersByProcessType(processType).length === 0) {
		return t("features.featureModeControls.noToolsForMaterial");
	}
	return "";
};

const surfaceButtonsShown = computed(() => {
	return (
		store.targetProcessType === ProcessType.FormCountersink ||
		store.targetProcessType === ProcessType.FormExtrudedThread ||
		store.targetProcessType === ProcessType.PressJoin ||
		(store.targetProcessType === ProcessType.DrillCylinderHeadBoltHole &&
			store.selectedFeatures.length > 0 &&
			store.selectedFeatures[0].featureType == FeatureType.Hole)
	);
});

const toolDropdownShown = computed(() => {
	return (
		availableFormingParametersForSide.value &&
		availableFormingParametersForSide.value.length > 0
	);
});

const pressJoinCategoryShown = computed(() => {
	return (
		toolDropdownShown &&
		store.areAnyFeaturesSelected && 
		store.areAllSelectedFeaturesOfSameType &&
		store.targetProcessType === ProcessType.PressJoin
	);
});

const noToolsForSelectedSideAvailable = computed(() => {
	return (
		availableFormingParametersForSide.value &&
		availableFormingParameters.value &&
		availableFormingParameters.value.length > 0 &&
		availableFormingParametersForSide.value.length === 0
	);
});

const anySettingsShown = computed(() => {
	return surfaceButtonsShown.value || toolDropdownShown.value;
});

const areCountersinksSelected = computed(() => {
	return (
		store.areAnyFeaturesSelected &&
		store.areAllSelectedFeaturesOfSameType &&
		store.selectedFeatures[0].featureType === FeatureType.Countersink
	);
});

const applyButtonDisabled = computed(() => {
	const isDisabled =
		store.article.isReadOnly ||
		!store.areAllSelectedFeaturesOfSameType ||
		(currentProcessType.value === ProcessType.Engrave &&
			store.areAnyFeaturesSelected &&
			store.selectedFeatures[0].isConfirmed) ||
		(currentProcessType.value !== ProcessType.Engrave &&
			currentProcessType.value === store.targetProcessType &&
			store.areAnyFeaturesSelected &&
			toolId.value === store.selectedFeatures[0].toolId &&
			surface.value === store.selectedFeatures[0].side &&
			// Bei Senkungen entspricht die Tool id schon der Vorgabe aus der Message, aber sie ist noch nicht bestätigt.
			((store.targetProcessType !== ProcessType.FormCountersink &&
				store.targetProcessType !== ProcessType.DrillCylinderHeadBoltHole) ||
				store.selectedFeatures[0].isConfirmed)) ||
		((store.targetProcessType === ProcessType.FormCountersink ||
			store.targetProcessType === ProcessType.FormExtrudedThread ||
			store.targetProcessType === ProcessType.DrillCylinderHeadBoltHole ||
			store.targetProcessType === ProcessType.PressJoin ||
			store.targetProcessType === ProcessType.FormScrewThread) &&
			(!toolId.value ||
				!availableFormingParametersForSide.value.some((p) => p.toolId === toolId.value)));
	return isDisabled;
});

const modifyFeatures = async () => {
	if (
		store.areAnyFeaturesSelected &&
		store.areAllSelectedFeaturesOfSameType &&
		store.targetProcessType === ProcessType.Cut &&
		store.selectedFeatures[0].featureType === FeatureType.Hole &&
		store.selectedFeatures[0].toolId !== null
	) {
		console.log("special case with hole that matches a well known diameter.");
		// Special case where the user declines a hole that matches a well known diameter
		store.setLoadingState(LoadingState.SettingFeature);
		const originalIds = store.cam.getOriginalExportIdsForSelectedFeatures(
			store.selectedFeatures.flatMap((f) => f.exportIds)
		);
		await BlexonErpApi.markAsHole({
			articleId: store.article.id,
			exportIds: originalIds,
		});
		//   const newFeatures = cloneDeep(features)
		//   const newValidationMessages = cloneDeep(validationMessages)
		for (const selectedFeature of store.selectedFeatures) {
			let storeFeature = store.features.find(
				(f) => f.featureId === selectedFeature.featureId
			);
			if (storeFeature) {
				storeFeature.toolId = "";
			}
			const messages = store.validationMessages.filter(
				(v) => v.featureIds && v.featureIds.includes(selectedFeature.featureId)
			);
			for (const message of messages) {
				message.featureIds = message.featureIds.filter(
					(id) => id !== selectedFeature.featureId
				);
				if (message.featureIds.length === 0) {
					message.state = MessageState.Declined;
				}
			}
		}
		store.isFileSaved = false;
		store.setLoadingState(LoadingState.Done);
		return;
	}

	store.setLoadingState(LoadingState.SettingFeature);

	// Defer execution of the heavy logic to allow UI update
	setTimeout(() => {
		const tasks: ITaskDto[] = [];
		for (const feature of store.selectedFeatures) {
			const featureHelper = FeatureHelper.create(feature);
			const task = featureHelper.getTaskForTargetProcessType(
				store.targetProcessType,
				toolId.value ?? "",
				surface.value
			);
			if (task !== null) {
				tasks.push(task);
			}
		}

		store.cam.modifyFeatures(LoadingState.SettingFeature, tasks);
	}, 0);
};

watch(store.selectedFeatures, onSelectedFeaturesChanged);
watch(() => store.targetProcessType, onTargetProcessChanged);
</script>
